import { useState, useMemo } from 'react';
import { X, Search, MessageSquare } from 'lucide-react';
import Avatar from '../ui/Avatar';
import { findConversationByContactAndChannel } from '../../utils/conversations';

export default function CreateConversationModal({
  open,
  onClose,
  channels = [],
  contacts = [],
  conversations = [],
  getContactForConversation,
  onCreateConversation,
}) {
  const channelList = Array.isArray(channels) ? channels : [];
  const [step, setStep] = useState('channel'); // 'channel' | 'contact'
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [searchContact, setSearchContact] = useState('');

  const handleClose = () => {
    setStep('channel');
    setSelectedChannel(null);
    setSearchContact('');
    onClose();
  };

  const handleSelectChannel = (channelId) => {
    setSelectedChannel(channelId);
    setStep('contact');
  };

  /** Contatos que ainda não têm conversa no canal selecionado */
  const contactsWithoutConversation = useMemo(() => {
    if (!selectedChannel || !Array.isArray(contacts)) return [];
    return contacts.filter((c) => {
      const existing = findConversationByContactAndChannel(
        conversations,
        c.id,
        selectedChannel,
        getContactForConversation
      );
      return !existing;
    });
  }, [contacts, conversations, selectedChannel, getContactForConversation]);

  const filteredContacts = useMemo(() => {
    let list = contactsWithoutConversation;
    const q = searchContact.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          (c.nome || '').toLowerCase().includes(q) ||
          (c.telefone || '').includes(q) ||
          (c.email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [contactsWithoutConversation, searchContact]);

  const handleSelectContact = (contact) => {
    onCreateConversation?.(contact.id, selectedChannel);
    handleClose();
  };

  const channelLabel = channelList.find((ch) => ch.id === selectedChannel)?.label || selectedChannel;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
      onKeyDown={(e) => e.key === 'Escape' && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-conversation-title"
    >
      <div
        className="bg-appSurface rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-appSurfaceBorder shrink-0">
          <h2 id="create-conversation-title" className="text-lg font-bold text-appText flex items-center gap-2">
            <MessageSquare size={20} className="text-appPrimary" />
            Nova conversa
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {step === 'channel' && (
            <div className="p-4 overflow-y-auto">
              <p className="text-sm text-appTextMuted mb-4">Selecione o canal da conversa:</p>
              {channelList.length === 0 ? (
                <p className="text-sm text-appTextMuted py-4 text-center">
                  Nenhum canal configurado. Crie canais no painel admin.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {channelList.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => handleSelectChannel(ch.id)}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-appSurfaceBorder hover:border-appPrimary/50 hover:bg-appPrimary/5 text-appText font-medium transition-colors"
                    >
                      {ch.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'contact' && (
            <>
              <div className="p-4 border-b border-appSurfaceBorder shrink-0 space-y-3">
                <button
                  type="button"
                  onClick={() => { setStep('channel'); setSelectedChannel(null); }}
                  className="text-sm text-appPrimary hover:underline"
                >
                  ← Trocar canal
                </button>
                <p className="text-sm text-appTextMuted">
                  Contatos sem conversa em <strong className="text-appText">{channelLabel}</strong>. Selecione um:
                </p>
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-appTextMuted" />
                  <input
                    type="search"
                    value={searchContact}
                    onChange={(e) => setSearchContact(e.target.value)}
                    placeholder="Buscar por nome, telefone ou e-mail..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText text-sm focus:ring-2 focus:ring-appPrimary outline-none bg-white"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 min-h-0">
                {filteredContacts.length === 0 ? (
                  <div className="py-12 text-center text-appTextMuted">
                    <p className="font-medium">
                      {contactsWithoutConversation.length === 0
                        ? 'Todos os contatos já possuem conversa neste canal.'
                        : 'Nenhum contato encontrado com essa busca.'}
                    </p>
                    <p className="text-sm mt-1">
                      {contactsWithoutConversation.length === 0
                        ? 'Cadastre um novo contato na aba Contatos.'
                        : 'Tente outro termo.'}
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {filteredContacts.map((contact) => {
                      const initials = (contact.nome || '')
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .substring(0, 2)
                        .toUpperCase() || '?';
                      return (
                        <li key={contact.id}>
                          <button
                            type="button"
                            onClick={() => handleSelectContact(contact)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-appSurfaceBorder/30 text-left transition-colors"
                          >
                            <Avatar label={initials} size="md" color="bg-appSurfaceBorder/50" />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-appText truncate">{contact.nome || '—'}</p>
                              <p className="text-xs text-appTextMuted truncate">
                                {[contact.telefone, contact.email].filter(Boolean).join(' • ') || '—'}
                              </p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
